// src/pages/storePages/manageStore/OrderDetailDialog.jsx
import { useEffect, useRef, useState } from "react";
import { storeGetOrder } from "../../../storeAPIs/StoreOrdersApis";
import "../../../static/storecss/manageStore/OrderDetailDialog.css";

const METHOD_LABELS = {
  mpesa: "M-Pesa",
  bank: "Bank Transfer",
  paypal: "PayPal",
};

export default function OrderDetailDialog({
  open,
  orderId,
  onClose,
  onConfirm,
  onCancel,
  readOnly = false,
}) {
  const dialogRef = useRef(null);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Open / close the native <dialog>
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;

    if (open && !dlg.open) {
      dlg.showModal();
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  // Fetch the order whenever the dialog opens or the id changes
  useEffect(() => {
    if (!open || !orderId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOrder(null);

    storeGetOrder(orderId)
      .then((res) => {
        if (!cancelled) setOrder(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 401) setError("Please log in to view this order.");
        else if (status === 403)
          setError("You don't have access to this order.");
        else if (status === 404) setError("Order not found.");
        else setError(err.message || "Could not load order.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, orderId]);

  const handleBackdropClick = (e) => {
    if (e.target === dialogRef.current && !busy) onClose?.();
  };

  const handleConfirm = async () => {
    if (!order || busy) return;
    setBusy(true);
    try {
      await onConfirm?.(order);
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!order || busy) return;
    if (!window.confirm(`Cancel order #${order.id}?`)) return;
    setBusy(true);
    try {
      await onCancel?.(order);
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const confirmed = Boolean(order?.is_confirmed);
  const cancelled = order?.status === "cancelled";
  const methodLabel = order
    ? METHOD_LABELS[order.payment_method] ||
      order.payment_method_display ||
      order.payment_method
    : "";

  const showActions = !readOnly && !confirmed && !cancelled;
  const showClose = readOnly || confirmed || cancelled;

  return (
    <dialog
      ref={dialogRef}
      className="o-dlg"
      onClick={handleBackdropClick}
      aria-labelledby="o-dlg-title"
    >
      <div className="o-dlg-inner">
        {/* ══ Header ══ */}
        <header className="o-dlg-header">
          <div className="o-dlg-header-text">
            <h2 id="o-dlg-title">
              {order ? `Order #${order.id}` : "Order"}
            </h2>
            {order && (
              <p>
                Placed {formatDate(order.created_at)}
                {order.username ? ` by ${order.username}` : ""}
              </p>
            )}
          </div>

          <div className="o-dlg-header-actions">
            {order && (
              <span
                className={`o-dlg-status ${
                  cancelled
                    ? "is-cancelled"
                    : confirmed
                    ? "is-confirmed"
                    : "is-pending"
                }`}
              >
                {cancelled
                  ? "Cancelled"
                  : confirmed
                  ? "Confirmed"
                  : "Pending"}
              </span>
            )}
            <button
              type="button"
              className="o-dlg-close"
              onClick={onClose}
              aria-label="Close"
              disabled={busy}
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </header>

        {/* ══ Body ══ */}
        <div className="o-dlg-body">
          {loading ? (
            <div className="o-dlg-loading">
              <span className="o-dlg-spinner" />
              Loading order…
            </div>
          ) : error ? (
            <div className="o-dlg-error">{error}</div>
          ) : order ? (
            <>
              {/* ── Items ── */}
              <section className="o-dlg-section">
                <h3 className="o-dlg-section-title">
                  <i className="fa-solid fa-box" /> Items
                  <span className="o-dlg-count">{order.items.length}</span>
                </h3>

                <ul className="o-dlg-items">
                  {order.items.map((it) => (
                    <li key={it.id} className="o-dlg-item">
                      <div className="o-dlg-item-thumb">
                        {it.primary_image ? (
                          <img
                            src={it.primary_image}
                            alt={it.product_name}
                            loading="lazy"
                          />
                        ) : (
                          <span className="o-dlg-item-thumb-ph">
                            <i className="fa-solid fa-image" />
                          </span>
                        )}
                      </div>

                      <div className="o-dlg-item-info">
                        <span className="o-dlg-item-name">
                          {it.product_name}
                        </span>
                        <span className="o-dlg-item-meta">
                          KES {Number(it.unit_price).toLocaleString()} ×{" "}
                          {it.quantity}
                        </span>
                      </div>

                      <span className="o-dlg-item-subtotal">
                        KES {Number(it.subtotal).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="o-dlg-total-row">
                  <span>Total</span>
                  <strong>
                    KES {Number(order.total).toLocaleString()}
                  </strong>
                </div>
              </section>

              {/* ── Payment ── */}
              <section className="o-dlg-section">
                <h3 className="o-dlg-section-title">
                  <i className="fa-solid fa-receipt" /> Payment
                </h3>
                <dl className="o-dlg-meta">
                  <div className="o-dlg-meta-row">
                    <dt>Method</dt>
                    <dd>
                      <span
                        className={`o-dlg-method o-dlg-method--${order.payment_method}`}
                      >
                        {methodLabel}
                      </span>
                    </dd>
                  </div>
                  <div className="o-dlg-meta-row">
                    <dt>Reference</dt>
                    <dd>
                      {order.transaction_id ? (
                        <code>{order.transaction_id}</code>
                      ) : (
                        <span className="o-dlg-muted">—</span>
                      )}
                    </dd>
                  </div>
                  <div className="o-dlg-meta-row">
                    <dt>Status</dt>
                    <dd>
                      <span
                        className={`o-dlg-status o-dlg-status--inline ${
                          cancelled
                            ? "is-cancelled"
                            : confirmed
                            ? "is-confirmed"
                            : "is-pending"
                        }`}
                      >
                        {cancelled
                          ? "Cancelled"
                          : confirmed
                          ? "Confirmed"
                          : "Pending"}
                      </span>
                    </dd>
                  </div>
                </dl>
              </section>

              {/* ── Customer ── */}
              <section className="o-dlg-section">
                <h3 className="o-dlg-section-title">
                  <i className="fa-solid fa-user" /> Customer
                </h3>
                <div className="o-dlg-customer">
                  <div className="o-dlg-customer-name">
                    {order.customer_name || order.username || "—"}
                  </div>
                  {order.customer_phone && (
                    <div className="o-dlg-customer-line">
                      <i className="fa-solid fa-phone" />
                      {order.customer_phone}
                    </div>
                  )}
                  {order.customer_email && (
                    <div className="o-dlg-customer-line">
                      <i className="fa-solid fa-envelope" />
                      {order.customer_email}
                    </div>
                  )}
                </div>
              </section>

              {/* ── Delivery note ── */}
              {order.delivery_note && (
                <section className="o-dlg-section">
                  <h3 className="o-dlg-section-title">
                    <i className="fa-solid fa-truck-fast" /> Delivery note
                  </h3>
                  <p className="o-dlg-note">{order.delivery_note}</p>
                </section>
              )}
            </>
          ) : null}
        </div>

        {/* ══ Footer ══ */}
        {order && (showActions || showClose) && (
          <footer className="o-dlg-footer">
            <div className="o-dlg-footer-actions">
              {showActions && (
                <>
                  <button
                    type="button"
                    className="o-dlg-btn o-dlg-btn--ghost"
                    onClick={handleCancel}
                    disabled={busy}
                  >
                    Cancel order
                  </button>
                  <button
                    type="button"
                    className="o-dlg-btn o-dlg-btn--primary"
                    onClick={handleConfirm}
                    disabled={busy}
                  >
                    <i className="fa-solid fa-check" />
                    {busy ? "Saving…" : "Confirm payment"}
                  </button>
                </>
              )}
              {showClose && (
                <button
                  type="button"
                  className="o-dlg-btn o-dlg-btn--ghost"
                  onClick={onClose}
                  disabled={busy}
                >
                  Close
                </button>
              )}
            </div>
          </footer>
        )}
      </div>
    </dialog>
  );
}